import { Component } from '@theme/component';

/**
 * @typedef {Object} PromoSliderRefs
 * @property {HTMLElement} viewport - Scroll-snapping row of slides
 * @property {HTMLElement[]} slides - One element per promo
 * @property {HTMLButtonElement[]} [dots] - One dot per promo, in slide order
 * @property {HTMLElement} [current] - Current slide number in the counter
 * @property {HTMLElement} [total] - Slide count in the counter
 */

/** Time after an automatic move during which its scroll events are not treated as a swipe. */
const AUTO_SCROLL_SETTLE_MS = 1000;

/**
 * Promo slider: native scroll snapping for swipes, plus a counter, dots and autoplay.
 *
 * Autoplay (data-autoplay, seconds) pauses while the slider is hovered, focused, off
 * screen or in a background tab, stops for good once the shopper swipes or picks a dot,
 * and never runs with reduced motion. Slides hidden at the current screen size (desktop
 * only promos are hidden with CSS) are left out of the counter, dots and rotation.
 *
 * On phones, slides after the first carry a blank <source data-deferred> so their images
 * don't compete with the first slide while the page loads; they are released on the
 * window load event, or earlier when the shopper starts swiping.
 *
 * @extends {Component<PromoSliderRefs>}
 */
class PromoSliderComponent extends Component {
  requiredRefs = ['viewport'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {number | undefined} */
  #autoplayTimeout;

  /** @type {number} */
  #scrollFrame = 0;

  /** @type {number} */
  #refreshFrame = 0;

  /** @type {number} */
  #index = 0;

  /** @type {HTMLElement | null} Slide the controls currently show */
  #shownSlide = null;

  /** @type {boolean} */
  #isRtl = false;

  /** @type {number} */
  #autoScrollUntil = 0;

  /** @type {HTMLElement | null} */
  #autoTarget = null;

  /** @type {boolean} */
  #paused = false;

  /** @type {boolean} */
  #stopped = false;

  /** @type {boolean} */
  #inView = true;

  #desktopQuery = window.matchMedia('(min-width: 750px)');
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /**
   * Scrolls to the slide that belongs to the clicked dot.
   * @param {Event} event - Click event from a dot button
   */
  goToSlide(event) {
    const dot = event.target instanceof Element ? event.target.closest('button') : null;
    if (!dot) return;

    const slide = this.refs.slides?.[Number(dot.dataset.index)];
    const index = slide ? this.#visibleSlides.indexOf(slide) : -1;
    if (index < 0) return;

    this.#stop();
    this.#scrollTo(index);
  }

  #setup() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.viewport.addEventListener('scroll', () => this.#handleScroll(), { passive: true, signal });
    this.addEventListener('pointerenter', () => this.#setPaused(true), { signal });
    this.addEventListener('pointerleave', () => this.#setPaused(false), { signal });
    this.addEventListener('focusin', () => this.#setPaused(true), { signal });
    this.addEventListener('focusout', () => this.#setPaused(false), { signal });
    document.addEventListener('visibilitychange', () => this.#scheduleAutoplay(), { signal });
    document.addEventListener('shopify:block:select', (event) => this.#handleBlockSelect(event), { signal });
    this.#desktopQuery.addEventListener('change', () => this.#refresh(), { signal });
    this.#reducedMotionQuery.addEventListener('change', () => this.#scheduleAutoplay(), { signal });

    if (document.readyState === 'complete') {
      this.#releaseDeferredSlides();
    } else {
      window.addEventListener('load', () => this.#releaseDeferredSlides(), { once: true, signal });
      this.refs.viewport.addEventListener('pointerdown', () => this.#releaseDeferredSlides(), { once: true, signal });
    }

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      this.#inView = entry?.isIntersecting ?? true;
      this.#scheduleAutoplay();
    });
    this.#intersectionObserver.observe(this);

    // Measure in the next frame, when the browser lays out anyway; measuring right away
    // forced an extra layout while the page was still loading.
    this.#refreshFrame = requestAnimationFrame(() => this.#refresh());
  }

  /** Removes the blank phone sources, so the browser loads those slides' images. */
  #releaseDeferredSlides() {
    for (const source of this.querySelectorAll('source[data-deferred]')) source.remove();
  }

  #teardown() {
    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
    window.clearTimeout(this.#autoplayTimeout);
    cancelAnimationFrame(this.#scrollFrame);
    cancelAnimationFrame(this.#refreshFrame);
  }

  /** @returns {HTMLElement[]} Slides rendered at the current screen size. */
  get #visibleSlides() {
    return (this.refs.slides ?? []).filter((slide) => slide.getClientRects().length > 0);
  }

  /** Syncs the dots, counter and static state with the slides shown at this screen size. */
  #refresh() {
    this.#isRtl = getComputedStyle(this).direction === 'rtl';
    this.#shownSlide = null;
    const visibleSlides = this.#visibleSlides;
    const { viewport, dots = [], total } = this.refs;
    const labelTemplate = this.dataset.dotLabel ?? '';

    // Nothing to rotate when every slide fits.
    this.toggleAttribute('data-static', viewport.scrollWidth <= viewport.clientWidth + 1);

    for (const [i, dot] of dots.entries()) {
      const slide = this.refs.slides?.[i];
      const position = slide ? visibleSlides.indexOf(slide) : -1;
      dot.hidden = position < 0;
      if (position >= 0) {
        const label = labelTemplate.replace('[index]', String(position + 1)).replace('[length]', String(visibleSlides.length));
        dot.setAttribute('aria-label', label);
      }
    }

    if (total) total.textContent = String(visibleSlides.length);

    this.#syncIndex();
    this.#scheduleAutoplay();
  }

  #handleScroll() {
    // A scroll that autoplay didn't start is a swipe, so the shopper is in control. Late
    // scroll events that land on autoplay's own target don't count.
    const autoplayArrived = this.#autoTarget !== null && this.#isAligned(this.#autoTarget);
    if (performance.now() > this.#autoScrollUntil && !autoplayArrived) this.#stop();

    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => this.#syncIndex());
  }

  /**
   * @param {HTMLElement} slide
   * @returns {number} Distance in px between the slide's start edge and the viewport's.
   */
  #distanceFromStart(slide) {
    const viewportRect = this.refs.viewport.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    return Math.abs(this.#isRtl ? rect.right - viewportRect.right : rect.left - viewportRect.left);
  }

  /** @param {HTMLElement} slide */
  #isAligned(slide) {
    return this.#distanceFromStart(slide) < 2;
  }

  /** Finds the slide closest to the viewport's start edge and reflects it in the controls. */
  #syncIndex() {
    const visibleSlides = this.#visibleSlides;

    let closest = 0;
    let closestDistance = Infinity;
    for (const [i, slide] of visibleSlides.entries()) {
      const distance = this.#distanceFromStart(slide);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = i;
      }
    }

    this.#index = closest;
    const currentSlide = visibleSlides[closest] ?? null;

    // Scroll fires every frame; only touch the DOM when the slide actually changes.
    if (currentSlide === this.#shownSlide) return;
    this.#shownSlide = currentSlide;

    if (this.refs.current) this.refs.current.textContent = String(closest + 1);

    for (const [i, dot] of (this.refs.dots ?? []).entries()) {
      dot.setAttribute('aria-current', String(this.refs.slides?.[i] === currentSlide));
    }
  }

  /** @param {number} index - Position among the visible slides */
  #scrollTo(index) {
    const slide = this.#visibleSlides[index];
    if (!slide) return;

    // Autoplay or a dot can move before the page has loaded.
    this.#releaseDeferredSlides();

    const { viewport } = this.refs;
    const offset = slide.getBoundingClientRect().left - viewport.getBoundingClientRect().left;
    viewport.scrollBy({ left: offset, behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth' });
  }

  #scheduleAutoplay() {
    window.clearTimeout(this.#autoplayTimeout);

    const seconds = Number(this.dataset.autoplay) || 0;
    const blocked =
      !seconds ||
      this.#stopped ||
      this.#paused ||
      !this.#inView ||
      document.hidden ||
      this.#reducedMotionQuery.matches ||
      this.hasAttribute('data-static');
    if (blocked) return;

    this.#autoplayTimeout = window.setTimeout(() => {
      // Read the position now rather than trusting the last scroll frame, which may not
      // have run yet (or at all in a background tab).
      this.#syncIndex();
      const count = this.#visibleSlides.length;
      if (count > 1) {
        const next = (this.#index + 1) % count;
        this.#autoScrollUntil = performance.now() + AUTO_SCROLL_SETTLE_MS;
        this.#autoTarget = this.#visibleSlides[next] ?? null;
        this.#scrollTo(next);
      }
      this.#scheduleAutoplay();
    }, seconds * 1000);
  }

  /** @param {boolean} paused */
  #setPaused(paused) {
    this.#paused = paused;
    this.#scheduleAutoplay();
  }

  #stop() {
    this.#stopped = true;
    window.clearTimeout(this.#autoplayTimeout);
  }

  /**
   * Theme editor: show the promo that was selected in the sidebar.
   * @param {Event} event
   */
  #handleBlockSelect(event) {
    const target = event.target instanceof Element ? event.target : null;
    const slide = target ? this.refs.slides?.find((item) => item === target || item.contains(target)) : undefined;
    if (!slide) return;

    const index = this.#visibleSlides.indexOf(slide);
    if (index < 0) return;

    this.#stop();
    this.#scrollTo(index);
  }
}

if (!customElements.get('promo-slider-component')) {
  customElements.define('promo-slider-component', PromoSliderComponent);
}
